import { Global } from "@emotion/react";
import { Chip, FormHelperText, Grid } from "@mui/material";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Typography from "@mui/material/Typography";
import { green, grey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { OutreachContactsContext } from "contexts/OutreachContacts";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "schemas/routes";

import StyledButton from "components/Styled/Button";

const drawerBleeding = 56;

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "light" ? "#fff" : grey[800],
}));

const Puller = styled(Box)(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: theme.palette.mode === "light" ? grey[300] : grey[900],
  borderRadius: 3,
  position: "absolute",
  top: 8,
  left: "calc(50% - 15px)",
}));

export default function HalfScreenPreviewCard() {
  const navigate = useNavigate();

  const [open, setOpen] = React.useState(false);
  const [fullScreen, setFullScreen] = React.useState(false);
  const { currentContact, currentContactScheduledEmails } = React.useContext(
    OutreachContactsContext,
  );

  const firstPendingEmail = currentContactScheduledEmails?.[0];

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  React.useEffect(() => {
    if (currentContact?.id) {
      setOpen(true);
    }
  }, [currentContact?.id]);

  const openFullScreen = () => {
    if (currentContact?.id && currentContact?.lastGmailThreadId) {
      navigate(
        `/${routes.inboxScheduled}/${currentContact.id}/thread/${currentContact?.lastGmailThreadId}`,
      );
    } else if (currentContact?.id) {
      navigate(`/${routes.inboxScheduled}/${currentContact.id}`);
    }
    setFullScreen(true);
  };

  const shouldOpenFullScreen = !fullScreen && currentContact?.id;
  React.useEffect(() => {
    if (shouldOpenFullScreen) {
      setFullScreen(true);
    }
  }, [shouldOpenFullScreen]);

  return (
    <Box>
      <CssBaseline />
      <Global
        styles={{
          ".MuiDrawer-root > .MuiPaper-root": {
            height:
              currentContact && open
                ? "fit-content"
                : `calc(50% - ${drawerBleeding}px)`,
            overflow: "visible",
          },
        }}
      />
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={drawerBleeding}
        disableSwipeToOpen={false}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <StyledBox
          sx={{
            position: "absolute",
            top: -drawerBleeding,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            visibility: "visible",
            right: 0,
            left: 0,
            boxShadow: currentContact && open ? 0 : 3,
          }}
        >
          {!(currentContact && open) && <Puller />}
          <Typography textAlign="center" sx={{ p: 3, color: "text.secondary" }}>
            {currentContact ? (
              <>
                Preview <strong>{currentContact?.brand?.name}</strong> emails
              </>
            ) : (
              `Click on a thread to preview scheduled emails`
            )}
          </Typography>
        </StyledBox>

        {currentContact && (
          <StyledBox
            sx={{
              p: 3,
              overflow: "auto",
            }}
          >
            <>
              <Puller />
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "primary.main",
                  mb: 2,
                }}
              >
                {currentContact?.brand?.name}
              </Typography>

              <Typography gutterBottom sx={{ mt: 2 }}>
                <strong>
                  {firstPendingEmail?.subject || firstPendingEmail?.contentType}
                </strong>
              </Typography>

              <Grid container alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                <FormHelperText sx={{ color: grey[500], fontSize: 10 }}>
                  To:
                </FormHelperText>

                <Chip
                  sx={{
                    fontSize: 10,
                    color: green[500],
                    border: `1px solid ${green[100]}`,
                  }}
                  size="small"
                  variant="outlined"
                  label={firstPendingEmail?.toEmail}
                />
              </Grid>

              <Typography sx={{ color: grey[500], mb: 3 }} gutterBottom>
                <Box
                  dangerouslySetInnerHTML={{
                    __html: firstPendingEmail?.body?.substring(0, 400) || "",
                  }}
                ></Box>
                ...
              </Typography>

              <StyledButton
                disableElevation
                fullWidth
                variant="contained"
                disabled={!currentContact?.id}
                onClick={openFullScreen}
              >
                Open Scheduled Emails
                <Box
                  component="i"
                  className="fa-regular fa-arrow-right"
                  sx={{ ml: 1 }}
                />
              </StyledButton>
            </>
          </StyledBox>
        )}
      </SwipeableDrawer>
    </Box>
  );
}
